/**
 * Viewport math for the infinite nested board.
 *
 * The viewport is CENTRE-CANONICAL: it stores the focal-space point currently
 * under the screen centre, not a screen-pixel offset. The screen transform is
 *   screen_x = width / 2  + (focal_x - center.x) * zoom
 *   screen_y = height / 2 + (focal_y - center.y) * zoom
 * i.e. `pan = size/2 - center * zoom` is DERIVED at render time via `panFor`.
 *
 * Why centre and not pan: pan is a screen-pixel quantity that only means
 * anything paired with a screen size, so it bakes the size in. Storing the
 * centre instead makes the viewport size-INDEPENDENT — a container resize
 * changes nothing in the stored value (the render re-derives pan from the new
 * size, holding the centre fixed and changing the sides), and persistence /
 * restore / cross-board sharing need no size at all. Size enters only where
 * pixels do: the render transform, gesture math (which already has the
 * cursor's screen coords), and screen projection.
 *
 * Zoom stays bounded across arbitrary nesting depth by rebasing the focal
 * space when an item dominates or the current focal becomes too small.
 */
import { type BoardItem, type BoardSpace } from "./types.js";

export interface Viewport {
  /** Sequence of space ids from root to current focal. focalPath[0] === rootSpace.id. */
  readonly focalPath: readonly string[];
  /** Focal-space point under the screen centre (size-independent). */
  readonly center: { readonly x: number; readonly y: number };
  readonly zoom: number;
  /**
   * The body rect (item-local model units) used to ENTER each focal level
   * beyond the root — `focalRects[i]` is the container body rect that
   * `pushFocal` used to dive from `focalPath[i]` into `focalPath[i+1]`.
   * Length is `focalPath.length - 1` when well-formed.
   *
   * This is the load-bearing fix for the dive/pop "teleport". Dive-in
   * measures the container body when its LOD band is expanded (zoomed in);
   * pop-out happens while zooming OUT, when that same band has collapsed
   * and a re-measurement fails or returns a different rect. Re-measuring on
   * pop therefore made `pushFocal` and `popFocal` non-inverse, so every
   * dive→pop cycle left residual pan/zoom error that accumulated into a
   * visible jump back toward root. Carrying the exact entry rect here lets
   * `popFocal` reuse it, making push/pop exact inverses regardless of the
   * band state, zoom, or DOM-measurement timing at pop time.
   *
   * Optional: viewports created by direct jumps (restore from storage,
   * `navigateTo`, `flyTo`) may omit it; `popFocal` then falls back to a
   * fresh measurement / the proportional `innerContentRect` as before.
   */
  readonly focalRects?: readonly ItemLocalRect[];
}

/** Minimum zoom before we try to rebase up (focal smaller than this fraction of screen). */
export const REBASE_OUT_THRESHOLD = 0.5;
/** Width of the well's zoom-ramp border band, as a fraction of the focal size
 *  past its edge: the total-zoom ceiling ramps from "no clamp" to ZOOM_MAX over
 *  this span as the screen centre climbs out. */
export const REANCHOR_PAN_MARGIN = 0.25;
/** Reanchor (pop to parent) once the focal covers less than this fraction of the
 *  screen — panned off while climbing out, or zoomed small. Kept low so the pop
 *  fires only when the child is nearly gone, hiding its focal→preview re-render. */
export const REANCHOR_COVERAGE = 0.1;
/** An item dominates when its projected size exceeds this fraction of
 *  screen. Tuned so dive-in fires naturally within the zoom range
 *  the user can reach by pinching (capped by ZOOM_MAX) rather than
 *  requiring a double-click — at 0.65, a container that fills 65%
 *  of the viewport is treated as "the user wants to be inside it". */
export const REBASE_IN_THRESHOLD = 0.65;

/**
 * Source of solved spaces, keyed by focal path.
 *
 * The board no longer assumes a single fully-solved `root` tree. A
 * `SpaceSource` resolves any focal path (a sequence of space ids from root) to
 * its solved `BoardSpace` on demand — backed either by the lazy `BoardEngine`
 * (live board: solves + caches the focal subtree only) or by walking an eager
 * tree (diff / embed / tests, via `rootSource`). All viewport math below takes
 * a source instead of a root, so the same code drives both.
 *
 * `at` is total: an invalid / unreachable path resolves to the deepest space it
 * can reach (root for a bad prefix), matching the old `resolveFocal` fallback.
 * `depth` requests how many nested levels below the focal to solve (the live
 * source uses it to render previews + feed dive-in math; default is the
 * source's own choice — `rootSource` ignores it since the eager tree is whole).
 */
export interface SpaceSource {
  at(path: readonly string[], depth?: number): BoardSpace;
}

/** Either a whole solved tree (eager) or a lazy source. The viewport math
 *  accepts both and normalises via `asSource`, so existing callers that pass a
 *  `BoardSpace` root keep working unchanged while the live board opts into an
 *  engine-backed `SpaceSource`. */
export type SpaceLike = BoardSpace | SpaceSource;

function isSource(x: SpaceLike): x is SpaceSource {
  return typeof (x as SpaceSource).at === "function";
}

/** Normalise a `SpaceLike` to a `SpaceSource` (wrapping a bare root tree). */
export function asSource(x: SpaceLike): SpaceSource {
  return isSource(x) ? x : rootSource(x);
}

/** Adapter: serve a fully-solved eager `root` tree as a `SpaceSource`. Keeps
 *  diff / embed / tests (and the drag/animation paths, which still build an
 *  eager tree) on the exact old tree-walk semantics. */
export function rootSource(root: BoardSpace): SpaceSource {
  return {
    at(path) {
      if (path.length === 0 || path[0] !== root.id) return root;
      let cur: BoardSpace = root;
      for (let i = 1; i < path.length; i++) {
        const item = cur.items.find((it) => it.space?.id === path[i]);
        if (!item?.space) return cur;
        cur = item.space;
      }
      return cur;
    },
  };
}

/** Resolve a focalPath to the space it points at. */
export function resolveFocal(src: SpaceLike, path: readonly string[]): BoardSpace {
  return asSource(src).at(path);
}

/** Walk focal path, returning [space, containerItem?][]. Container is the item
 *  at the previous level whose inner space is this one. */
export interface FocalStep {
  readonly space: BoardSpace;
  readonly containerItem?: BoardItem;
}
export function walkFocal(src: SpaceLike, path: readonly string[]): FocalStep[] {
  if (path.length === 0) return [];
  const source = asSource(src);
  const steps: FocalStep[] = [{ space: source.at(path.slice(0, 1)) }];
  for (let i = 1; i < path.length; i++) {
    const parent = source.at(path.slice(0, i));
    const container = parent.items.find((it) => it.space?.id === path[i]);
    if (!container?.space) break;
    steps.push({ space: source.at(path.slice(0, i + 1)), containerItem: container });
  }
  return steps;
}

/**
 * Build the `focalRects` stack for a focal path reached by a direct jump
 * (`navigateTo` / `flyTo`) rather than a dive sequence. There is no measured
 * rect for a jump, so each level uses the deterministic proportional
 * `innerContentRect` of its container — which makes a later `popFocal` an
 * exact inverse of the (proportional) transform, keeping pop-out off the
 * collapsed-band re-measurement path. Returns one rect per transition
 * (length = path.length - 1).
 */
export function focalRectsForPath(
  src: SpaceLike,
  path: readonly string[],
): ItemLocalRect[] {
  return walkFocal(src, path)
    .map((step) => step.containerItem)
    .filter((it): it is BoardItem => it !== undefined)
    .map((it) => innerContentRect(it));
}

/** Compute the viewport that centers the root space on screen. */
export function initialViewport(root: BoardSpace, width: number, height: number): Viewport {
  // Guard degenerate inputs: a not-yet-laid-out root (0 width/height) or a
  // hidden / unmeasured container makes `width / 0` → Infinity → a NaN pan that
  // blanks the board. Fall back to a finite 1:1 centred identity; a later fit
  // (real layout + measured size) replaces it.
  const safeW = root.width > 0 ? root.width : 1;
  const safeH = root.height > 0 ? root.height : 1;
  const fit = Math.min(width / safeW, height / safeH);
  const zoom = Number.isFinite(fit) && fit > 0 ? 0.9 * fit : 1;
  // Centre the rectangle (originX..originX+width, originY..originY+height)
  // on screen — not just (0..width, 0..height).
  const cx = root.originX + safeW / 2;
  const cy = root.originY + safeH / 2;
  return { focalPath: [root.id], focalRects: [], center: { x: cx, y: cy }, zoom };
}

/**
 * Derive the screen-pixel pan offset for a viewport at a given screen size.
 * The single bridge from the size-independent centre to the render transform:
 *   screen = pan + world * zoom    (the classic form the renderer applies)
 * with `pan = size/2 - center * zoom`.
 */
export function panFor(
  vp: Viewport,
  width: number,
  height: number,
): { x: number; y: number } {
  return {
    x: width / 2 - vp.center.x * vp.zoom,
    y: height / 2 - vp.center.y * vp.zoom,
  };
}

/** Apply wheel-delta zoom, pivoting at cursor (sx, sy) — the world point under
 *  the cursor stays fixed on screen. */
export function zoomAt(
  vp: Viewport,
  sx: number,
  sy: number,
  delta: number,
  width: number,
  height: number,
): Viewport {
  // delta > 0 → zoom in. Convert to ratio.
  const ratio = Math.exp(delta);
  const newZoom = vp.zoom * ratio;
  return applyZoomAtPivot(vp, newZoom, sx, sy, width, height);
}

/** Translate by a screen-pixel delta (drag-pan). A +dx screen shift of the
 *  content moves the centre by -dx/zoom in focal coords. */
export function pan(vp: Viewport, dx: number, dy: number): Viewport {
  return {
    ...vp,
    center: { x: vp.center.x - dx / vp.zoom, y: vp.center.y - dy / vp.zoom },
  };
}

/** The focal-space point under the screen centre — now just the stored value. */
export function viewportCenter(vp: Viewport): { x: number; y: number } {
  return vp.center;
}

/** Place the focal-space point (cx, cy) under the screen centre; zoom unchanged. */
export function centerViewport(vp: Viewport, cx: number, cy: number): Viewport {
  return { ...vp, center: { x: cx, y: cy } };
}

/**
 * Pan slack — how far past a screen edge the focal space may trail, as a
 * fraction of the screen dimension. Generous (0.7) — you can push well past
 * the edge; the containment regime below guarantees the space itself never
 * vanishes from view.
 */
export const PAN_SLACK = 0.7;

/**
 * The border around a space (root and nested alike): clamp the pan so the
 * VOID visible on any side never exceeds `slack` of the screen.
 *
 * Two regimes per axis:
 *  - space larger than the screen (zoomed in): the space must keep covering
 *    the screen up to a `slack` gap on either side — you can pan all over
 *    the inside, but never fly out;
 *  - space smaller than the screen (zoomed out): coverage is impossible, so
 *    it flips to containment — the space may roam the screen but can't
 *    leave any edge by more than `slack`.
 *
 * Pure; returns `vp` itself when already inside the bounds, so callers can
 * use identity to detect "was out". The gesture pipelines clamp every frame
 * and a settle-time rescue teleports back anything that still escaped
 * (restored viewport from another screen size, a buggy flight, …).
 */
export function clampPan(
  focal: BoardSpace,
  vp: Viewport,
  screen: { width: number; height: number },
  slack: number = PAN_SLACK,
): Viewport {
  if (screen.width <= 0 || screen.height <= 0) return vp;
  const z = vp.zoom;
  // Work in pan-space (the proven bounds maths), deriving pan from the centre
  // and converting any clamped result back to a centre at the end. The identity
  // path (`px === pan.x && py === pan.y`) returns the ORIGINAL `vp` object —
  // load-bearing for callers that test `clamped === vp` (touch inertia stop,
  // the out-of-bounds rescue).
  const panX = screen.width / 2 - vp.center.x * z;
  const panY = screen.height / 2 - vp.center.y * z;
  // Space rect in screen coords is [x1 + pan.x, x2 + pan.x] (same for y).
  const x1 = focal.originX * z;
  const x2 = (focal.originX + focal.width) * z;
  const y1 = focal.originY * z;
  const y2 = (focal.originY + focal.height) * z;

  const axis = (lo: number, hi: number, span: number, value: number): number => {
    const slackPx = span * slack;
    const size = hi - lo;
    let min: number;
    let max: number;
    if (size >= span) {
      // Space at least screen-sized: cover rule — the void on either side
      // may not exceed slack.
      min = span - slackPx - hi;
      max = slackPx - lo;
    } else {
      // Space smaller than the screen (zoomed out): containment rule. Exit
      // is capped at HALF THE SPACE'S OWN SIZE (not just slack): the space
      // stays at least half visible, so the project can never wander out
      // of sight no matter the zoom.
      const exit = Math.min(slackPx, size / 2);
      min = -exit - lo;
      max = span + exit - hi;
    }
    return Math.min(max, Math.max(min, value));
  };
  const px = axis(x1, x2, screen.width, panX);
  const py = axis(y1, y2, screen.height, panY);
  if (px === panX && py === panY) return vp;
  return {
    ...vp,
    center: {
      x: (screen.width / 2 - px) / z,
      y: (screen.height / 2 - py) / z,
    },
  };
}

/**
 * Product of the dive `fit` factors from the root down to the current focal.
 *
 * Diving multiplies the frame zoom by `fit` (`pushFocal`: newZoom = zoom·fit),
 * and `fit < 1`, so the frame-relative `zoom` SHRINKS as you nest even though
 * the view is visually identical. The cross-level "total" magnification is
 * therefore `frame_zoom / Πfit` — it's preserved across dive/pop and is what the
 * user's zoom cap (`ZOOM_MAX`) really governs: nested, you can push the frame
 * zoom to `ZOOM_MAX` and reach a total of `ZOOM_MAX / Πfit` (> ZOOM_MAX), i.e.
 * you can zoom DEEPER inside a subspace. Returns 1 at root.
 */
export function focalFitProduct(src: SpaceLike, vp: Viewport): number {
  if (vp.focalPath.length <= 1) return 1;
  const source = asSource(src);
  const steps = walkFocal(source, vp.focalPath);
  const rects = vp.focalRects;
  const wellFormed = rects != null && rects.length === vp.focalPath.length - 1;
  let pi = 1;
  for (let i = 1; i < steps.length; i++) {
    const container = steps[i]?.containerItem;
    if (!container) continue;
    const rect = (wellFormed ? rects[i - 1] : undefined) ?? innerContentRect(container);
    const inner = innerTransform(container, rect);
    if (inner && inner.fit > 0 && Number.isFinite(inner.fit)) pi *= inner.fit;
  }
  return pi > 0 && Number.isFinite(pi) ? pi : 1;
}

/**
 * The focal's PARENT space projected into the FOCAL coordinate system — the
 * region the gravity-well continuity lets you roam across without popping.
 *
 * When diven, the pan clamp uses this instead of the focal's own bounds, so you
 * can move freely over the whole parent while staying zoomed into the node; the
 * parent's edges (+ slack) still bound you. Returns null at root, on a malformed
 * focal stack, or if the projection degenerates — callers fall back to the focal
 * bounds (identical to the pre-gravity-well behaviour).
 *
 * The projection is the inverse of `pushFocal`'s centre map (parent coords →
 * focal coords): `Xf = (Xp − container.x − tx)/fit + originX`, using the SAME
 * stored entry rect that made dive/pop exact — so the roam region lines up with
 * where the context layer actually renders the parent.
 */
export function roamBounds(src: SpaceLike, vp: Viewport): BoardSpace | null {
  if (vp.focalPath.length <= 1) return null;
  const source = asSource(src);
  const steps = walkFocal(source, vp.focalPath);
  // Full consumption: reject a malformed / degenerate (dive-into-self) path, the
  // same guard the context layer uses. Otherwise the parent slice could resolve
  // to the focal itself and the roam region would collapse onto it.
  if (steps.length !== vp.focalPath.length) return null;
  const container = steps[steps.length - 1]?.containerItem;
  if (!container) return null;
  const stack = vp.focalRects;
  const rect =
    stack && stack.length === vp.focalPath.length - 1
      ? stack[stack.length - 1]!
      : innerContentRect(container);
  const inner = innerTransform(container, rect);
  if (!inner || !(inner.fit > 0) || !Number.isFinite(inner.fit)) return null;
  const parent = source.at(vp.focalPath.slice(0, -1));
  const toFocalX = (xp: number) => (xp - container.x - inner.tx) / inner.fit + inner.originX;
  const toFocalY = (yp: number) => (yp - container.y - inner.ty) / inner.fit + inner.originY;
  // Union the projected parent with the focal's OWN bounds so the roam region
  // ALWAYS contains the focal. Under the "space frame wraps its items" invariant
  // (types.ts) the projection already contains the focal, so the union is a
  // no-op then — but a stale container frame (dragged past its recomputed frame,
  // one paint before re-solve) could project a rect that clips the focal, which
  // would clamp the user INSIDE the focal (worse than pre-roam). The union makes
  // that impossible.
  const focal = container.space;
  if (!focal) return null;
  const x1 = Math.min(toFocalX(parent.originX), focal.originX);
  const y1 = Math.min(toFocalY(parent.originY), focal.originY);
  const x2 = Math.max(toFocalX(parent.originX + parent.width), focal.originX + focal.width);
  const y2 = Math.max(toFocalY(parent.originY + parent.height), focal.originY + focal.height);
  const width = x2 - x1;
  const height = y2 - y1;
  if (!(width > 0) || !(height > 0) || !Number.isFinite(width) || !Number.isFinite(height)) {
    return null;
  }
  return { id: `${parent.id}::roam`, items: [], originX: x1, originY: y1, width, height };
}

/**
 * Pan clamp for the interactive board: clamps against the gravity-well roam
 * region (the parent projected into focal coords) when diven, else the focal's
 * own bounds. A drop-in for `clampPan(resolveFocal(src, vp.focalPath), vp,
 * screen)` at every gesture call site — at root (roamBounds null) it is
 * byte-identical to the old focal clamp.
 */
export function clampPanRoam(
  src: SpaceLike,
  vp: Viewport,
  screen: { width: number; height: number },
  slack: number = PAN_SLACK,
): Viewport {
  const bounds = roamBounds(src, vp) ?? resolveFocal(src, vp.focalPath);
  return clampPan(bounds, vp, screen, slack);
}

/**
 * Fraction of an item's height reserved for the widget header / footer.
 * The body region where the nested space draws sits between the two.
 *
 * Matches the default `arch-module` widget's typical band proportions
 * at full zoom (header ~20%, footer ~10%, body fills the rest). Custom
 * widgets that diverge will get a visually approximate fit — the
 * trade-off keeps the dive-in/pop-out math free of DOM measurement.
 */
export const HEADER_FRACTION = 0.20;
export const FOOTER_FRACTION = 0.10;

/**
 * Rectangle (in item-local coords) where a nested space is drawn. Fixed
 * proportions — independent of zoom band — so the dive-in / pop-out math
 * matches the renderer regardless of how widgets internally lay out
 * their body / details bands.
 */
export function innerContentRect(item: BoardItem): {
  x: number; y: number; w: number; h: number;
} {
  const headerH = item.h * HEADER_FRACTION;
  const footerH = item.h * FOOTER_FRACTION;
  return { x: 0, y: headerH, w: item.w, h: item.h - headerH - footerH };
}

/** Item-local rectangle (in model units, not screen pixels). */
export interface ItemLocalRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Compute how an item's nested space is placed inside the item's body. Mirrors
 * the CSS transform applied by the renderer:
 *   translate(tx, ty) · scale(fit) · translate(-originX, -originY)
 *
 * The body rect can be passed in (preferred — measured from the
 * widget DOM at click time, guaranteeing pixel-perfect alignment with
 * what the user sees) or derived from `innerContentRect` as a
 * fallback when no measurement is available.
 */
function innerTransform(
  item: BoardItem,
  rect: ItemLocalRect,
): {
  fit: number;
  tx: number;
  ty: number;
  originX: number;
  originY: number;
} | null {
  if (!item.space) return null;
  const innerSpace = item.space;
  const sx = rect.w / innerSpace.width;
  const sy = rect.h / innerSpace.height;
  const fit = Math.min(sx, sy);
  const tx = rect.x + (rect.w - innerSpace.width * fit) / 2;
  const ty = rect.y + (rect.h - innerSpace.height * fit) / 2;
  return {
    fit,
    tx,
    ty,
    originX: innerSpace.originX,
    originY: innerSpace.originY,
  };
}

/** Push `item` (a child of current focal) as new focal; viewport stays visually identical. */
export function pushFocal(
  vp: Viewport,
  item: BoardItem,
  bodyRect?: ItemLocalRect,
): Viewport {
  if (!item.space) return vp;
  // Refuse to dive into the space we're already focused on. A repeated id in
  // focalPath ([root, X, X]) is always corruption — it breaks breadcrumb depth,
  // pop-out (walkFocal stops short), and the gravity-well parent slice. The live
  // trigger is the deferred push in `diveInto`: it fires `pushFocal(currentVp,
  // item)` in a rAF AFTER a framing animation, so a second double-click or an
  // auto-rebase that already entered the same item makes the deferred push
  // append the id twice. Guarding at the root keeps every caller safe.
  if (item.space.id === vp.focalPath[vp.focalPath.length - 1]) return vp;
  // Resolve the body rect ONCE here (measured if supplied, else proportional)
  // and store it on the focal stack so the matching `popFocal` can reuse the
  // exact same rect — guaranteeing push/pop are inverses. See `focalRects`.
  const rect = bodyRect ?? innerContentRect(item);
  const inner = innerTransform(item, rect);
  if (!inner) return vp;
  // Screen position of the nested space's logical (originX, originY) in the
  // current focal's coords = item.x + tx, item.y + headerH + ty. That point
  // must stay put after the rebase, which means the new focal's (originX,
  // originY) maps to the same screen position. Combined with newZoom = oldZoom
  // * fit, the pan expression falls out.
  const newZoom = vp.zoom * inner.fit;
  // Re-express the centred point in the new focal's coords. The screen centre
  // is unchanged, so its old-focal coord (vp.center) maps to the new-focal coord
  // via the inverse placement: Xn = (Xo - item.x - tx)/fit + originX. The screen
  // size cancels out of the old pan-based form, so no size is needed.
  const newCenter = {
    x: (vp.center.x - item.x - inner.tx) / inner.fit + inner.originX,
    y: (vp.center.y - item.y - inner.ty) / inner.fit + inner.originY,
  };
  return {
    focalPath: [...vp.focalPath, item.space.id],
    focalRects: [...(vp.focalRects ?? []), rect],
    center: newCenter,
    zoom: newZoom,
  };
}

/** Pop the current focal back up one level. container is the item in the parent whose inner space was the focal. */
export function popFocal(
  vp: Viewport,
  container: BoardItem,
  bodyRect?: ItemLocalRect,
): Viewport {
  if (vp.focalPath.length <= 1) return vp;
  // Prefer the rect that was used to ENTER this level (stored at dive time).
  // It is the only rect guaranteed to make this pop the exact inverse of the
  // dive — a fresh `bodyRect` measured now is read while the container's LOD
  // band is collapsing on the way out, so it disagrees and teleports. Only
  // fall back to the passed/proportional rect when the stack is absent or
  // malformed (e.g. a viewport restored from storage or reached by a direct
  // `navigateTo` / `flyTo` jump that never pushed).
  const stack = vp.focalRects;
  const wellFormed = stack != null && stack.length === vp.focalPath.length - 1;
  const rect = (wellFormed ? stack[stack.length - 1] : undefined)
    ?? bodyRect
    ?? innerContentRect(container);
  const inner = innerTransform(container, rect);
  if (!inner) return vp;
  const newZoom = vp.zoom / inner.fit;
  // Inverse of pushFocal's centre map: Xo = (Xn - originX)*fit + container.x + tx.
  const newCenter = {
    x: (vp.center.x - inner.originX) * inner.fit + container.x + inner.tx,
    y: (vp.center.y - inner.originY) * inner.fit + container.y + inner.ty,
  };
  return {
    focalPath: vp.focalPath.slice(0, -1),
    focalRects: wellFormed ? stack.slice(0, -1) : stack,
    center: newCenter,
    zoom: newZoom,
  };
}

/** Zoom limits. Past these, rebase-in / rebase-out would take over anyway. */
export const ZOOM_MAX = 5;
export const ZOOM_MIN = 0.1;

function applyZoomAtPivot(
  vp: Viewport,
  newZoom: number,
  pivotX: number,
  pivotY: number,
  width: number,
  height: number,
): Viewport {
  // Keep the world point under (pivotX, pivotY) fixed on screen across the zoom
  // change. World point = center + (pivot - size/2)/zoom; the new centre puts it
  // back under the same pixel at the new zoom.
  const wx = vp.center.x + (pivotX - width / 2) / vp.zoom;
  const wy = vp.center.y + (pivotY - height / 2) / vp.zoom;
  return {
    ...vp,
    zoom: newZoom,
    center: {
      x: wx - (pivotX - width / 2) / newZoom,
      y: wy - (pivotY - height / 2) / newZoom,
    },
  };
}

/**
 * Clamp zoom at the edges of what rebasing can handle.
 *
 * Zoom-in is only useful if the viewport will rebase deeper — so when the
 * cursor isn't over an item with a nested space, cap at ZOOM_MAX.
 * Zoom-out is only useful if there's a parent to pop — so at root level, cap
 * at ZOOM_MIN to keep the board visible.
 */
export function clampZoom(
  src: SpaceLike,
  vp: Viewport,
  pivotX: number,
  pivotY: number,
  width: number,
  height: number,
): Viewport {
  if (vp.focalPath.length === 1 && vp.zoom < ZOOM_MIN) {
    return applyZoomAtPivot(vp, ZOOM_MIN, pivotX, pivotY, width, height);
  }
  if (vp.zoom > ZOOM_MAX) {
    const focal = resolveFocal(src, vp.focalPath);
    const focalPx = vp.center.x + (pivotX - width / 2) / vp.zoom;
    const focalPy = vp.center.y + (pivotY - height / 2) / vp.zoom;
    const hit = focal.items.find(
      (it) =>
        focalPx >= it.x &&
        focalPx <= it.x + it.w &&
        focalPy >= it.y &&
        focalPy <= it.y + it.h,
    );
    if (!hit?.space) {
      return applyZoomAtPivot(vp, ZOOM_MAX, pivotX, pivotY, width, height);
    }
  }
  return vp;
}

/**
 * Body-rect resolver injected by the caller. Returns the body-slot rect
 * (item-local coords) for a container item, or undefined to fall back to
 * the proportional `innerContentRect`. App-shell passes the pure solver
 * rect (`subspaceSlotRect` via its `diveBodyRect` seam — no DOM); kept as
 * a parameter so `maybeRebase` stays a pure function of its inputs and
 * tests can pin the fallback path by passing nothing.
 */
export type MeasureBodyRect = (item: BoardItem) => ItemLocalRect | null | undefined;

/** Decide whether to automatically rebase (dive-in or pop-out) based on viewport state.
 *  When called from a cursor-driven gesture (pinch / wheel zoom), pass
 *  the cursor's screen coords as `pivot`; dive-in then chases what the
 *  user is aiming at instead of demanding it dominate the viewport
 *  centre. Without a pivot we fall back to centre — used by drag-pan
 *  and keyboard zoom where there is no cursor reference.
 *
 *  `measure` injects DOM body-rect measurement; omit it for a pure
 *  (proportional-fallback) rebase. Keeping the DOM read as an injected
 *  argument is what lets callers run this *outside* a React state
 *  updater — updaters must stay pure / side-effect-free. */
export function maybeRebase(
  src: SpaceLike,
  vp: Viewport,
  screen: { width: number; height: number },
  pivot?: { x: number; y: number },
  measure?: MeasureBodyRect,
): Viewport {
  const source = asSource(src);
  let current = vp;
  // Loop up to a small limit — guards against pathological inputs, otherwise
  // each step shrinks or grows the zoom until the thresholds are satisfied.
  for (let i = 0; i < 16; i++) {
    const next = rebaseStep(source, current, screen, pivot, measure);
    if (next === current) break;
    current = next;
  }
  return current;
}

function rebaseStep(
  src: SpaceSource,
  vp: Viewport,
  screen: { width: number; height: number },
  pivot?: { x: number; y: number },
  measure?: MeasureBodyRect,
): Viewport {
  // Dive-in trigger: an item dominates (covers more than REBASE_IN_THRESHOLD
  // of the screen width) and either (a) the cursor pivot is over it, or
  // (b) no pivot was supplied and it covers the viewport centre.
  const focal = resolveFocal(src, vp.focalPath);
  const aimX = pivot?.x ?? screen.width / 2;
  const aimY = pivot?.y ?? screen.height / 2;
  const focalAimX = vp.center.x + (aimX - screen.width / 2) / vp.zoom;
  const focalAimY = vp.center.y + (aimY - screen.height / 2) / vp.zoom;

  for (const item of focal.items) {
    if (!item.space) continue;
    const screenW = item.w * vp.zoom;
    if (screenW < REBASE_IN_THRESHOLD * screen.width) continue;
    if (
      focalAimX < item.x ||
      focalAimX > item.x + item.w ||
      focalAimY < item.y ||
      focalAimY > item.y + item.h
    ) continue;
    return pushFocal(vp, item, measure?.(item) ?? undefined);
  }

  // Gravity WELL — the zoom-cap ramp. Nested, you can push the TOTAL zoom
  // (frame_zoom / Πfit) past ZOOM_MAX (you're deep in a subspace). The well's
  // wall is a ramp on the total-zoom CEILING as the screen centre pans past the
  // focal edge: from "no extra clamp" inside, down to ZOOM_MAX by the far side of
  // the border band (REANCHOR_PAN_MARGIN of the focal size past the edge). So the
  // further out you go, the further you zoom out — but ONLY the excess above
  // ZOOM_MAX ramps away; if your total is already ≤ ZOOM_MAX you cross freely.
  // Reaching the far side reanchors (pops) into the parent, seamlessly, because
  // the total is now exactly ZOOM_MAX (the parent's own ambient limit).
  //
  // At deep zoom the focal is far larger than the screen, so the centre stays
  // inside it however far you pan → out = 0 → no ramp, you roam freely.
  if (vp.focalPath.length > 1) {
    let cur = vp;
    // Pan-only (no cursor pivot): a pinch-zoom must not be fought by the cap.
    if (!pivot) {
      const outX =
        Math.max(0, focal.originX - vp.center.x, vp.center.x - (focal.originX + focal.width)) /
        focal.width;
      const outY =
        Math.max(0, focal.originY - vp.center.y, vp.center.y - (focal.originY + focal.height)) /
        focal.height;
      const out = Math.max(outX, outY);
      if (out > 0) {
        const piFit = focalFitProduct(src, vp);
        const t = Math.min(out / REANCHOR_PAN_MARGIN, 1);
        // Frame-zoom cap so TOTAL = frame/piFit ramps to ZOOM_MAX at t = 1:
        //   totalCeil(t) = lerp(ZOOM_MAX/piFit, ZOOM_MAX, t)
        //   frameCap     = totalCeil · piFit = ZOOM_MAX·(1 − t·(1 − piFit))
        // Direct in `t` (not lerp-from-current) ⇒ a min-cap that's idempotent, so
        // re-running inside the maybeRebase loop is stable. At t = 0 the cap is
        // ZOOM_MAX (the normal per-frame limit — no bite unless you were already
        // deeper than ambient); at t = 1 it forces total down to ZOOM_MAX.
        const frameCap = ZOOM_MAX * (1 - t * (1 - piFit));
        if (frameCap < vp.zoom) cur = { ...vp, zoom: frameCap };
      }
    }
    // Reanchor when the focal has mostly LEFT the screen — either panned off
    // (climbing out) or zoomed small (surface-out). Deferring the pop until the
    // child is nearly gone hides the focal→preview render swap: the child renders
    // full-detail as a focal but fit into the container's body slot as a nested
    // preview, so popping while it's still on-screen visibly shifts it. The
    // parent behind (context layer) is already at the identical transform, so the
    // pop itself is seamless — only the child's re-render is the seam, and by the
    // time it's this far off it's an off-screen sliver.
    //
    // NOTE (measured): the swap is byte-seamless ONLY when popFocal's fit
    // (`innerTransform(container, storedDiveRect)`) equals the preview's measured
    // fit. They match once you've climbed far out (the space has settled to a
    // stable solve) but DIVERGE by up to ~0.9 at higher zoom / earlier pops,
    // because `container.space` re-solves to different bounds than the dive-time
    // rect was measured against. Popping only at low coverage (child ~gone) keeps
    // the residual off-screen. Fixing it for arbitrary on-screen pops needs
    // dive/preview to share one stable space+slot — see the crossing plan.
    //
    // Coverage is the ON-SCREEN (clipped) area fraction — NOT the full projected
    // rect area — so a large focal panned off-screen correctly reads as gone.
    const z = cur.zoom;
    const fLeft = screen.width / 2 + (focal.originX - cur.center.x) * z;
    const fTop = screen.height / 2 + (focal.originY - cur.center.y) * z;
    const fRight = fLeft + focal.width * z;
    const fBottom = fTop + focal.height * z;
    const visW = Math.max(0, Math.min(fRight, screen.width) - Math.max(fLeft, 0));
    const visH = Math.max(0, Math.min(fBottom, screen.height) - Math.max(fTop, 0));
    const cov = (visW * visH) / Math.max(screen.width * screen.height, 1);
    if (cov < REANCHOR_COVERAGE) {
      const steps = walkFocal(src, vp.focalPath);
      const container = steps[steps.length - 1]?.containerItem;
      if (container) {
        return popFocal(cur, container, measure?.(container) ?? undefined);
      }
    }
    // Ramp applied but not yet across — commit the zoom-out and let the next
    // frame carry it further as you keep panning out.
    if (cur !== vp) return cur;
  }

  return vp;
}

/** Animate vp1 → vp2 at time t ∈ [0, 1]. Assumes same focalPath. */
export function lerpViewport(a: Viewport, b: Viewport, t: number): Viewport {
  // Logarithmic zoom interpolation feels natural.
  const lnZoom = Math.log(a.zoom) + (Math.log(b.zoom) - Math.log(a.zoom)) * t;
  return {
    focalPath: b.focalPath,
    focalRects: b.focalRects,
    center: {
      x: a.center.x + (b.center.x - a.center.x) * t,
      y: a.center.y + (b.center.y - a.center.y) * t,
    },
    zoom: Math.exp(lnZoom),
  };
}

/** A planned pan/zoom animation: sample with `at(t)`, run for ~`duration` ms. */
export interface ViewportFlight {
  /** Viewport at t ∈ [0, 1]. at(0) ≈ from, at(1) = to (exactly). */
  at(t: number): Viewport;
  /** Suggested duration (ms) — proportional to the perceptual flight distance,
   *  so a small nudge is quick and a deep dive takes its time. Clamped. */
  readonly duration: number;
}

export interface FlightOptions {
  /** Pan/zoom trade-off (van Wijk's ρ). Higher ⇒ zooms out more on long pans.
   *  √2 is the paper's perceptual optimum. */
  readonly rho?: number;
  /** Duration per unit of flight distance S. */
  readonly msPerUnit?: number;
  readonly minMs?: number;
  readonly maxMs?: number;
}

/**
 * Plan a smooth, comfortable pan-zoom flight from `a` to `b` (same focalPath)
 * — van Wijk & Nuij's "Smooth and efficient zooming and panning" (the same
 * trajectory d3's interpolateZoom uses).
 *
 * Why not `lerpViewport`: lerping pan linearly in *screen* space while zoom
 * changes exponentially makes the world point under the screen centre swerve
 * mid-flight, and a fixed short duration makes a deep zoom race — both read as
 * nausea. The flight instead moves the *world-space centre* along a hyperbolic
 * path that zooms out just enough to keep apparent motion uniform (a long pan
 * arcs out and back in; a pure zoom glides), and reports a duration
 * proportional to the perceptual distance travelled.
 */
export function planFlight(
  a: Viewport,
  b: Viewport,
  screen: { width: number; height: number },
  opts: FlightOptions = {},
): ViewportFlight {
  const rho = opts.rho ?? Math.SQRT2;
  const msPerUnit = opts.msPerUnit ?? 440;
  const minMs = opts.minMs ?? 200;
  const maxMs = opts.maxMs ?? 1000;
  const W = Math.max(screen.width, 1);
  const H = Math.max(screen.height, 1);

  // Express each viewport as (world centre, visible world width).
  const cx0 = a.center.x;
  const cy0 = a.center.y;
  const w0 = W / a.zoom;
  const cx1 = b.center.x;
  const cy1 = b.center.y;
  const w1 = W / b.zoom;

  const dx = cx1 - cx0;
  const dy = cy1 - cy0;
  const d2 = dx * dx + dy * dy;
  const d1 = Math.sqrt(d2);
  const rho2 = rho * rho;

  // Rebuild a viewport from (centre, visible width), carrying b's focal stack
  // (same convention as lerpViewport).
  const view = (cx: number, cy: number, w: number): Viewport => {
    const zoom = W / w;
    return {
      focalPath: b.focalPath,
      focalRects: b.focalRects,
      center: { x: cx, y: cy },
      zoom,
    };
  };

  let S: number;
  let at: (t: number) => Viewport;

  if (d2 < 1e-6) {
    // Centres coincide — a pure zoom glide (exponential in w).
    S = Math.abs(Math.log(w1 / w0)) / rho;
    at = (t) =>
      t >= 1 ? view(cx1, cy1, w1) : view(cx0 + dx * t, cy0 + dy * t, w0 * Math.exp(Math.log(w1 / w0) * t));
  } else {
    const rho4 = rho2 * rho2;
    const b0 = (w1 * w1 - w0 * w0 + rho4 * d2) / (2 * w0 * rho2 * d1);
    const b1 = (w1 * w1 - w0 * w0 - rho4 * d2) / (2 * w1 * rho2 * d1);
    const r0 = Math.log(Math.sqrt(b0 * b0 + 1) - b0);
    const r1 = Math.log(Math.sqrt(b1 * b1 + 1) - b1);
    S = (r1 - r0) / rho;
    at = (t) => {
      if (t >= 1) return view(cx1, cy1, w1);
      const s = t * S;
      const coshr0 = Math.cosh(r0);
      const u = (w0 / (rho2 * d1)) * (coshr0 * Math.tanh(rho * s + r0) - Math.sinh(r0));
      const w = (w0 * coshr0) / Math.cosh(rho * s + r0);
      return view(cx0 + u * dx, cy0 + u * dy, w);
    };
  }

  const duration = Math.min(maxMs, Math.max(minMs, Math.abs(S) * msPerUnit));
  return { at, duration };
}

/** Viewport that frames a given child item at ~90% of screen. */
export function frameItem(
  vp: Viewport,
  item: BoardItem,
  screen: { width: number; height: number },
  pad = 0.1,
): Viewport {
  return frameRect(vp, { x: item.x, y: item.y, w: item.w, h: item.h }, screen, pad);
}

/**
 * Viewport that frames an arbitrary rect (in the current focal's coords) at
 * ~(1 - pad) of the screen, centred. The generalisation of `frameItem` to a
 * region — used by the camera operator to hold a *set* of nodes (a multi-edit
 * blast radius, a multi-selection) in view, not just one item. Keeps `vp`'s
 * focalPath/focalRects; only pan/zoom change.
 */
export function frameRect(
  vp: Viewport,
  rect: ItemLocalRect,
  screen: { width: number; height: number },
  pad = 0.1,
): Viewport {
  // Guard against a zero-area rect (a single empty/degenerate node) so zoom
  // stays finite — frame it at whatever the other axis allows.
  const w = Math.max(rect.w, 1e-6);
  const h = Math.max(rect.h, 1e-6);
  const zoomX = ((1 - pad) * screen.width) / w;
  const zoomY = ((1 - pad) * screen.height) / h;
  const newZoom = Math.min(zoomX, zoomY);
  // Centre the rect on screen: the focal point under the screen centre is the
  // rect's own centre. Size-independent (the screen dims only set the zoom fit).
  const newCenter = { x: rect.x + rect.w / 2, y: rect.y + rect.h / 2 };
  return { ...vp, center: newCenter, zoom: newZoom };
}

/** Axis-aligned bounding rect (focal coords) enclosing every item. Null when
 *  the set is empty. */
export function boundingRect(items: readonly BoardItem[]): ItemLocalRect | null {
  if (items.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const it of items) {
    minX = Math.min(minX, it.x);
    minY = Math.min(minY, it.y);
    maxX = Math.max(maxX, it.x + it.w);
    maxY = Math.max(maxY, it.y + it.h);
  }
  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

/** How a focal-coords rect projects onto the screen under `vp`. */
export interface RectVisibility {
  /** Fraction of the screen area the rect covers (clamped to [0, 1]). */
  readonly coverage: number;
  /** True when the rect lies fully within the screen, inset by `margin` px. */
  readonly fullyInside: boolean;
}

/**
 * Project a focal-coords rect through `vp` and report how it sits on screen —
 * the camera operator's "is this already comfortably in frame?" test. A rect
 * that's fully inside with a sensible coverage means the user can see what
 * they're editing, so the operator should stay put rather than chase it.
 */
export function rectVisibility(
  vp: Viewport,
  rect: ItemLocalRect,
  screen: { width: number; height: number },
  margin = 0,
): RectVisibility {
  const left = screen.width / 2 + (rect.x - vp.center.x) * vp.zoom;
  const top = screen.height / 2 + (rect.y - vp.center.y) * vp.zoom;
  const right = left + rect.w * vp.zoom;
  const bottom = top + rect.h * vp.zoom;
  const screenArea = Math.max(screen.width * screen.height, 1e-6);
  const rectArea = Math.max((right - left) * (bottom - top), 0);
  const coverage = Math.min(rectArea / screenArea, 1);
  const fullyInside =
    left >= margin &&
    top >= margin &&
    right <= screen.width - margin &&
    bottom <= screen.height - margin;
  return { coverage, fullyInside };
}
